import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../components/Button";
import Input, { MaterialInputEvent } from "../../../../components/v2/Input";
import Modal from "../../../../components/v2/Modal";
import NoticeDialog from "../../../../components/v2/NoticeModal/NoticeDialog";
import AdminNoticesService, {
  AdminNotice,
  AdminNoticeErrors,
  AdminNoticeForm,
  AdminNoticeSummary,
  noticeFailure,
} from "../../../../services/AdminNoticesService";
import { defaultPeriod, formatDateTime, inputValueToIso, isoToInputValue } from "./dates";
import FormattingHelp from "./FormattingHelp";

import style from "./styles.module.scss";

const keyI18n = "translations.admin.notices.form";
const TITLE_LIMIT = 120;
const BODY_LIMIT = 1500;
const LABEL_LIMIT = 40;
const DEFAULT_CONFIRMATION = "Entendi";
const URL_PATTERN = /^https?:\/\/[^\s<>"]+$/i;
const LINK_PATTERN = /\[[^\]\n]+\]\(([^)\s]*)\)/g;

interface Props {
  open: boolean;
  notice: AdminNotice | null;
  onClose(): void;
  onSaved(notice: AdminNotice, created: boolean): void;
}

type Values = AdminNoticeForm;

function initialValues(notice: AdminNotice | null): Values {
  if (!notice) {
    return { title: "", body: "", confirmationLabel: DEFAULT_CONFIRMATION, actionLabel: "", actionUrl: "", ...defaultPeriod() };
  }

  return {
    title: notice.title,
    body: notice.body,
    confirmationLabel: notice.confirmationLabel,
    actionLabel: notice.actionLabel || "",
    actionUrl: notice.actionUrl || "",
    startsAt: isoToInputValue(notice.startsAt),
    endsAt: isoToInputValue(notice.endsAt),
  };
}

function bodyLinksValid(body: string): boolean {
  const urls: string[] = [];
  body.replace(LINK_PATTERN, (match, url: string) => {
    urls.push(url);
    return match;
  });
  return urls.every((url) => URL_PATTERN.test(url));
}

export default function NoticeFormDialog({ open, notice, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const editing = notice !== null;

  const [values, setValues] = useState<Values>(initialValues(null));
  const [errors, setErrors] = useState<AdminNoticeErrors>({});
  const [conflict, setConflict] = useState<AdminNoticeSummary | null>(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setValues(initialValues(notice));
    setErrors({});
    setConflict(null);
    setFailed(false);
    setSaving(false);
    setPreviewing(false);
  }, [open, notice]);

  if (!open) {
    return null;
  }

  const set = (field: keyof Values) => (event: MaterialInputEvent) => {
    const { value } = event.currentTarget;
    setValues((current) => ({ ...current, [field]: value }));
  };

  const close = () => {
    if (!saving) {
      onClose();
    }
  };

  const errorText = (field: string) => {
    const codes = errors[field];
    if (!codes || codes.length === 0) {
      return undefined;
    }
    return t(`${keyI18n}.errors.${field}.${codes[0]}`, { defaultValue: t(`${keyI18n}.errors.invalid`) });
  };

  const validate = (): AdminNoticeErrors => {
    const found: AdminNoticeErrors = {};
    if (!values.title.trim()) found.title = ["blank"];
    if (!values.body.trim()) found.body = ["blank"];
    else if (!bodyLinksValid(values.body)) found.body = ["link"];
    if (!values.confirmationLabel.trim()) found.confirmationLabel = ["blank"];
    if (!values.startsAt) found.startsAt = ["blank"];
    if (!values.endsAt) found.endsAt = ["blank"];
    else if (values.startsAt && values.endsAt <= values.startsAt) found.endsAt = ["before_start"];
    const actionLabel = values.actionLabel.trim();
    const actionUrl = values.actionUrl.trim();
    if (actionUrl && !actionLabel) found.actionLabel = ["blank"];
    if (actionLabel && !actionUrl) found.actionUrl = ["blank"];
    else if (actionUrl && !URL_PATTERN.test(actionUrl)) found.actionUrl = ["invalid"];
    return found;
  };

  const submit = (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }
    if (saving) {
      return;
    }

    const found = validate();
    setErrors(found);
    setConflict(null);
    setFailed(false);
    if (Object.keys(found).length > 0) {
      return;
    }

    const form: AdminNoticeForm = {
      ...values,
      title: values.title.trim(),
      body: values.body.trim(),
      confirmationLabel: values.confirmationLabel.trim(),
      actionLabel: values.actionLabel.trim(),
      actionUrl: values.actionUrl.trim(),
      startsAt: inputValueToIso(values.startsAt),
      endsAt: inputValueToIso(values.endsAt),
    };

    setSaving(true);
    const request = notice ? AdminNoticesService.update(notice.id, form) : AdminNoticesService.create(form);
    request
      .then((saved) => onSaved(saved, !notice))
      .catch((error) => {
        const failure = noticeFailure(error);
        if (failure) {
          setErrors(failure.errors);
          setConflict(failure.conflict || null);
        } else {
          setFailed(true);
        }
        setSaving(false);
      });
  };

  const baseErrors = errors.base || [];
  const preview = {
    title: values.title.trim() || t(`${keyI18n}.previewEmptyTitle`),
    text: values.body,
    confirmation_button: values.confirmationLabel.trim() || DEFAULT_CONFIRMATION,
    action: values.actionLabel.trim() && values.actionUrl.trim()
      ? { label: values.actionLabel.trim(), url: values.actionUrl.trim() }
      : null,
  };

  return (
    <>
      <Modal
        open
        onClose={close}
        dismissible={!saving && !previewing}
        icon={editing ? "edit_note" : "campaign"}
        title={t(`${keyI18n}.${editing ? "editTitle" : "createTitle"}`)}
        size="large"
        actions={(
          <>
            <Button
              variant="text"
              size="small"
              className={style.previewButton}
              onClick={() => setPreviewing(true)}
              disabled={saving}
              icon={<span className="material-symbols-sharp">visibility</span>}
            >
              {t(`${keyI18n}.preview`)}
            </Button>
            <Button variant="text" size="small" onClick={close} disabled={saving}>
              {t(`${keyI18n}.cancel`)}
            </Button>
            <Button variant="filled" size="small" onClick={() => submit()} disabled={saving} softDisabled={saving}>
              {t(`${keyI18n}.${saving ? "saving" : "save"}`)}
            </Button>
          </>
        )}
      >
        <form className={style.form} noValidate onSubmit={submit}>
          {notice && notice.status !== "draft" && (
            <p className={`pgm-body-medium ${style.publishedHint}`}>
              <span aria-hidden="true" className="material-symbols-sharp">info</span>
              {t(`${keyI18n}.publishedHint`)}
            </p>
          )}

          <div className={style.field}>
            <Input
              variant="outlined"
              label={t(`${keyI18n}.title`)}
              value={values.title}
              maxLength={TITLE_LIMIT}
              required
              autoFocus
              autoComplete="off"
              fullWidth
              error={Boolean(errorText("title"))}
              errorText={errorText("title")}
              onInput={set("title")}
            />
          </div>

          <div className={style.field}>
            <Input
              variant="outlined"
              type="textarea"
              label={t(`${keyI18n}.body`)}
              value={values.body}
              rows={7}
              maxLength={BODY_LIMIT}
              required
              fullWidth
              hideClearButton
              supportingText={t(`${keyI18n}.bodyHint`)}
              error={Boolean(errorText("body"))}
              errorText={errorText("body")}
              onInput={set("body")}
            />
          </div>
          <FormattingHelp />

          <fieldset className={style.group}>
            <legend className={`pgm-title-small ${style.legend}`}>{t(`${keyI18n}.period`)}</legend>
            <div className={style.row}>
              <div className={style.field}>
                <Input
                  variant="outlined"
                  type="datetime-local"
                  label={t(`${keyI18n}.startsAt`)}
                  value={values.startsAt}
                  required
                  fullWidth
                  hideClearButton
                  error={Boolean(errorText("startsAt"))}
                  errorText={errorText("startsAt")}
                  onInput={set("startsAt")}
                />
              </div>
              <div className={style.field}>
                <Input
                  variant="outlined"
                  type="datetime-local"
                  label={t(`${keyI18n}.endsAt`)}
                  value={values.endsAt}
                  required
                  fullWidth
                  hideClearButton
                  error={Boolean(errorText("endsAt"))}
                  errorText={errorText("endsAt")}
                  onInput={set("endsAt")}
                />
              </div>
            </div>
          </fieldset>

          <fieldset className={style.group}>
            <legend className={`pgm-title-small ${style.legend}`}>{t(`${keyI18n}.buttons`)}</legend>
            <div className={style.row}>
              <div className={style.field}>
                <Input
                  variant="outlined"
                  label={t(`${keyI18n}.confirmationLabel`)}
                  value={values.confirmationLabel}
                  maxLength={LABEL_LIMIT}
                  required
                  autoComplete="off"
                  fullWidth
                  error={Boolean(errorText("confirmationLabel"))}
                  errorText={errorText("confirmationLabel")}
                  onInput={set("confirmationLabel")}
                />
              </div>
              <div className={style.field}>
                <Input
                  variant="outlined"
                  label={t(`${keyI18n}.actionLabel`)}
                  value={values.actionLabel}
                  maxLength={LABEL_LIMIT}
                  autoComplete="off"
                  fullWidth
                  supportingText={t(`${keyI18n}.optional`)}
                  error={Boolean(errorText("actionLabel"))}
                  errorText={errorText("actionLabel")}
                  onInput={set("actionLabel")}
                />
              </div>
            </div>
            <div className={style.field}>
              <Input
                variant="outlined"
                type="url"
                label={t(`${keyI18n}.actionUrl`)}
                value={values.actionUrl}
                autoComplete="off"
                fullWidth
                supportingText={t(`${keyI18n}.actionUrlHint`)}
                error={Boolean(errorText("actionUrl"))}
                errorText={errorText("actionUrl")}
                onInput={set("actionUrl")}
              />
            </div>
          </fieldset>

          {conflict && (
            <p className={`pgm-body-medium ${style.formError}`} role="alert">
              {t(`${keyI18n}.errors.base.overlap`, {
                title: conflict.title,
                start: formatDateTime(conflict.startsAt),
                end: formatDateTime(conflict.endsAt),
              })}
            </p>
          )}
          {(failed || (baseErrors.length > 0 && !conflict)) && (
            <p className={`pgm-body-medium ${style.formError}`} role="alert">
              {t(`${keyI18n}.errors.generic`)}
            </p>
          )}
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
      </Modal>

      <NoticeDialog notice={preview} open={previewing} onClose={() => setPreviewing(false)} />
    </>
  );
}
